/**
 * Sidebar Component
 * Requirements: 8.1, 8.2, 8.5, 8.6, 17.1, 17.2, 17.3, 17.4 - Role-based navigation
 */
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
// import QuizIcon from '@mui/icons-material/Quiz'; // 非表示化した SELF_ONLY メニュー(質問回答/GBなび今週の回答)でのみ使用していたため無効化
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleIcon from '@mui/icons-material/People';
import PatternIcon from '@mui/icons-material/Pattern';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SyncIcon from '@mui/icons-material/Sync';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import type { AuthUser } from '../../../features/auth/types/auth.types';
import { useCompanySettings } from '../../hooks/useCompanySettings';

export const DRAWER_WIDTH = 280;

export interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  requiredPermission?: 'SELF_ONLY' | 'DEPARTMENT' | 'COMPANY_WIDE';
  adminOnly?: boolean;
  /** 表示に必要な購入機能。未指定なら購入状況に依らず表示。 */
  requiredProduct?: 'jinzai' | 'soshiki' | 'navi';
  /** レポート系メニュー（レポート開示 reportVisible OFF で非表示）。 */
  report?: boolean;
  /**
   * デモ専用メニュー。ビルド時フラグ VITE_SHOW_DEMO_MENU='true' のときだけ表示する。
   * dev 配信では true を焼き込み、prod では未設定(=非表示)にして本番リリースまで隠す。
   */
  demoOnly?: boolean;
  /** クリック時アクション（指定時は画面遷移でなくこの処理を実行。例: テンプレDL）。 */
  onClick?: () => void;
}

const PERMISSION_HIERARCHY = {
  SELF_ONLY: 0,
  DEPARTMENT: 1,
  COMPANY_WIDE: 2,
};

// Menu items grouped by category
// 以下3項目はメニューから非表示（要望により除外）。再表示する場合は各行と
// 上部の QuizIcon import のコメントを解除すること。
//   質問回答(/questionnaire) / 自分の分析結果(/analysis/my-results) / GBなび 今週の回答(/terasu/answer)
const SELF_ONLY_MENU_ITEMS: MenuItem[] = [
  // { label: '質問回答', path: '/questionnaire', icon: <QuizIcon />, requiredProduct: 'jinzai' },
  // {
  //   label: '自分の分析結果',
  //   path: '/analysis/my-results',
  //   icon: <AssessmentIcon />,
  //   requiredProduct: 'jinzai',
  // },
  // {
  //   label: 'GBなび 今週の回答',
  //   path: '/terasu/answer',
  //   icon: <QuizIcon />,
  //   requiredProduct: 'navi',
  // },
];

// Note: DEPARTMENT_MENU_ITEMS removed per Requirements 8.1, 8.2, 8.3
// The following menus are no longer displayed:
// - 部門内思考傾向分布
// - 部門内ネットワーク図
// - 部門内分析結果一覧

const COMPANY_WIDE_MENU_ITEMS: MenuItem[] = [
  {
    // GB組織 診断レポート。2026-08-22 に v3 へ差し替え。
    // 旧b7(/analysis/org-report)はルートを残しているので、比較したいときは
    // URL直打ちで開ける（モバイルの /embed/report/org-legacy と同じ扱い）。
    label: '組織レポート',
    path: '/analysis/org-report-v3',
    icon: <AssessmentIcon />,
    requiredPermission: 'COMPANY_WIDE',
    requiredProduct: 'soshiki',
    report: true,
  },
  // 「組織レポート(20名デモ)」はメニュー非表示（要望）。ルート/コンポーネントは残置。
  {
    // 社員一覧→クリックで個人レポート(最新desc結線済み Gb74PersonalReport)を新規タブで表示
    label: '個人分析一覧',
    path: '/analysis/company-results',
    icon: <AssessmentIcon />,
    requiredPermission: 'COMPANY_WIDE',
    requiredProduct: 'jinzai',
    report: true,
  },
  {
    // GBなび 経営者レポート 週報 (別画面)
    label: 'GBなび 週報',
    path: '/terasu/report/weekly',
    icon: <AssessmentIcon />,
    requiredPermission: 'COMPANY_WIDE',
    requiredProduct: 'navi',
    report: true,
  },
  {
    // GBなび 経営者レポート 月報 (別画面)
    label: 'GBなび 月報',
    path: '/terasu/report/monthly',
    icon: <AssessmentIcon />,
    requiredPermission: 'COMPANY_WIDE',
    requiredProduct: 'navi',
    report: true,
  },
  {
    // GBなび レポート(デモ) — 週報/月報をフロントのダミーデータで確認するデモ画面。
    // 表示条件: (a) ビルド時フラグ VITE_SHOW_DEMO_MENU='true'（dev配信のみ true）
    //           (b) 設定連動: navi購入 かつ レポート開示(reportVisible) ON かつ 社長
    label: 'GBなび レポート(デモ)',
    path: '/terasu/report-demo',
    icon: <AssessmentIcon />,
    requiredPermission: 'COMPANY_WIDE',
    requiredProduct: 'navi',
    report: true,
    demoOnly: true,
  },
  {
    // サービスの活用方法(PDF)。社長(COMPANY_WIDE)向け。
    // PDFは docsBucket(CloudFront /docs/*)配信。相対パスで dev/prod 双方の同一ドメインに解決する。
    // 差し替えは同一キーへの再アップロード＋invalidation のみ（アプリ再デプロイ不要）。
    label: 'サービスの活用方法',
    path: '#service-guide', // 遷移せず onClick でPDFを別タブ表示（キー用の擬似パス）
    icon: <MenuBookIcon />,
    requiredPermission: 'COMPANY_WIDE',
    onClick: () => window.open('/docs/service-guide.pdf', '_blank', 'noopener'),
  },
  {
    label: 'アカウント情報',
    path: '/account',
    icon: <AccountBoxIcon />,
    requiredPermission: 'COMPANY_WIDE',
  },
  // 「GBなび レポート(デモ)」は上に復活（設定連動: navi×reportVisible）。
  // 以下の旧メニューは非表示 (gbpav130 由来の旧画面。ルート/コンポーネントは残置):
  // - 全社思考傾向分布 (/aggregation/company/distribution)
  // - 組織分析 (/aggregation/company/network)
  // - 個人分析 (/analysis/company-results)
];

const ADMIN_MENU_ITEMS: MenuItem[] = [
  {
    label: '質問セット管理',
    path: '/admin/question-sets',
    icon: <ListAltIcon />,
    adminOnly: true,
  },
  {
    label: '会社管理',
    path: '/admin/companies',
    icon: <BusinessIcon />,
    adminOnly: true,
  },
  {
    label: '部門管理',
    path: '/admin/departments',
    icon: <GroupIcon />,
    adminOnly: true,
  },
  {
    label: '社員管理',
    path: '/admin/employees',
    icon: <AdminPanelSettingsIcon />,
    adminOnly: true,
  },
  {
    label: '権限管理',
    path: '/admin/permissions',
    icon: <AdminPanelSettingsIcon />,
    adminOnly: true,
  },
  {
    label: 'ユーザー一括登録',
    path: '/admin/bulk-registration',
    icon: <PersonAddIcon />,
    adminOnly: true,
  },
  {
    label: 'ユーザー管理',
    path: '/admin/users',
    icon: <PeopleIcon />,
    adminOnly: true,
  },
  {
    label: '判定パターン管理',
    path: '/admin/judgment-patterns',
    icon: <PatternIcon />,
    adminOnly: true,
  },
  {
    label: 'チーム判定パターン管理',
    path: '/admin/team-judgment-patterns',
    icon: <PatternIcon />,
    adminOnly: true,
  },
  {
    label: '分析結果管理',
    path: '/admin/analysis-results',
    icon: <AnalyticsIcon />,
    adminOnly: true,
  },
  {
    label: 'データ同期',
    path: '/admin/data-sync',
    icon: <SyncIcon />,
    adminOnly: true,
  },
  {
    label: 'QRコード発行',
    path: '/admin/qr-issuance',
    icon: <QrCode2Icon />,
    adminOnly: true,
  },
  {
    label: '回答実施（GB人財）',
    path: '/admin/answer-campaigns',
    icon: <EventAvailableIcon />,
    adminOnly: true,
  },
];

const hasPermission = (
  userPermission: AuthUser['permissionLevel'],
  requiredPermission: MenuItem['requiredPermission']
): boolean => {
  if (!requiredPermission) return true;
  if (!userPermission) return false;

  const userLevel = PERMISSION_HIERARCHY[userPermission] ?? -1;
  const requiredLevel = PERMISSION_HIERARCHY[requiredPermission] ?? 0;

  return userLevel >= requiredLevel;
};

export interface SidebarProps {
  user: AuthUser | null;
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ user, isOpen, onClose, isMobile = false }) => {
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const shouldUseMobile = isMobile || isSmallScreen;

  const userPermission = user?.permissionLevel;
  const isAdmin = user?.isAdmin ?? false;

  // 自社設定（購入機能／レポート開示）を取得。メニューの出し分けに使う。
  const settings = useCompanySettings(!!user);

  // 購入機能（requiredProduct）とレポート開示（report×reportVisible）でメニューを出し分ける。
  const isEntitled = (item: MenuItem): boolean => {
    // デモ専用メニューはビルド時フラグ VITE_SHOW_DEMO_MENU='true'（dev配信のみ）でのみ表示。
    if (item.demoOnly && import.meta.env.VITE_SHOW_DEMO_MENU !== 'true') return false;
    if (item.requiredProduct === 'jinzai' && !settings.hasJinzai) return false;
    if (item.requiredProduct === 'soshiki' && !settings.hasSoshiki) return false;
    if (item.requiredProduct === 'navi' && !settings.hasNavi) return false;
    if (item.report && !settings.reportVisible) return false;
    return true;
  };

  const getVisibleMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [...SELF_ONLY_MENU_ITEMS];

    // Note: DEPARTMENT_MENU_ITEMS removed per Requirements 8.1, 8.2, 8.3

    // COMPANY_WIDE_MENU_ITEMS はレポート系（組織レポート／個人分析一覧／GBなび週報・月報）。
    if (hasPermission(userPermission, 'COMPANY_WIDE')) {
      items.push(...COMPANY_WIDE_MENU_ITEMS);
    }

    // 購入機能・レポート開示で出し分け。
    return items.filter(isEntitled);
  };

  const visibleMenuItems = getVisibleMenuItems();
  const visibleAdminItems = isAdmin ? ADMIN_MENU_ITEMS : [];

  const handleItemClick = () => {
    if (shouldUseMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box
      role="navigation"
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ overflow: 'auto', flexGrow: 1, pt: 2 }}>
        <List>
          {visibleMenuItems.map((item) => {
            const isSelected = !item.onClick && location.pathname === item.path;

            return (
              <ListItem key={item.path} disablePadding className={isSelected ? 'Mui-selected' : ''}>
                {item.onClick ? (
                  // アクション項目（画面遷移せず onClick を実行。例: テンプレDL）
                  <ListItemButton
                    onClick={() => {
                      item.onClick?.();
                      handleItemClick();
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ) : (
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    selected={isSelected}
                    onClick={handleItemClick}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                )}
              </ListItem>
            );
          })}
        </List>

        {visibleAdminItems.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <List>
              {visibleAdminItems.map((item) => {
                const isSelected = location.pathname === item.path;

                return (
                  <ListItem
                    key={item.path}
                    disablePadding
                    className={isSelected ? 'Mui-selected' : ''}
                  >
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={isSelected}
                      onClick={handleItemClick}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Box>
    </Box>
  );

  if (shouldUseMobile) {
    return (
      <Drawer
        variant="temporary"
        open={isOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      open={isOpen}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          top: 64, // AppBar height
          height: 'calc(100% - 64px)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};
